// Pure creator-draft helpers for the "Openings" collection (formerly the
// single "First message" field). See `@/lib/conversationIntros` for the
// conversation-consumption side; this module owns the creator draft's array
// invariants: stable ids, immutable edits, and non-destructive validation.
//
// These helpers do not touch store/adapter wiring, autosave, or hydration —
// callers own composing them into the workspace draft.

import type { ConversationIntro } from '@/types/domain'
import { deriveLegacyIntro, isBlankIntroContent } from '@/lib/conversationIntros'

const MAX_OPENINGS = 10

function newOpeningId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `opening-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** True when an id is a usable, stable opaque identifier (non-empty string). */
function hasValidId(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/** Creates a new creator-authored opening with a fresh, stable UUID. */
export function createOpening(content = ''): ConversationIntro {
  return { id: newOpeningId(), content }
}

/**
 * Derives the one-item legacy collection a character's `initial_message`
 * represents, using the same deterministic `legacy-<characterId>` id scheme
 * the conversation read model and the Supabase backfill migration use.
 * Returns `[]` when the legacy message is blank.
 */
export const deriveLegacyOpenings = deriveLegacyIntro

/**
 * Normalizes an incoming (possibly loosely-typed) openings array — e.g. from
 * a JSON draft payload or workspace hydration — into well-formed
 * `ConversationIntro[]`.
 *
 * Idempotent: entries that already carry a valid, non-empty string `id` keep
 * that exact id. Only entries missing a valid id receive a freshly generated
 * one. This is load-bearing — re-normalizing an already-valid collection must
 * be a no-op on ids, or autosave snapshot comparison will see a "changed"
 * draft on every hydration and loop.
 */
export function normalizeOpenings(openings: unknown): ConversationIntro[] {
  if (!Array.isArray(openings)) return []

  return openings.map((raw) => {
    const entry = (raw && typeof raw === 'object' ? raw : {}) as Partial<ConversationIntro>
    const id = hasValidId(entry.id) ? entry.id : newOpeningId()
    const content = typeof entry.content === 'string' ? entry.content : String(entry.content ?? '')
    return { id, content }
  })
}

/** Finds an opening by its stable id. */
export function findOpeningById(openings: ConversationIntro[], id: string): ConversationIntro | undefined {
  return openings.find((opening) => opening.id === id)
}

/** Appends a new opening. Immutable: returns a new array. */
export function addOpening(openings: ConversationIntro[], content = ''): ConversationIntro[] {
  return [...openings, createOpening(content)]
}

/**
 * Moves the opening with `id` one position toward `direction` (`-1` left /
 * `-1` earlier, `1` right / later). Immutable. Returns the original array
 * reference, unchanged, when the id is missing or the move would go out of
 * bounds (already first/last).
 */
export function moveOpening(openings: ConversationIntro[], id: string, direction: -1 | 1): ConversationIntro[] {
  const index = openings.findIndex((opening) => opening.id === id)
  if (index === -1) return openings

  const targetIndex = index + direction
  if (targetIndex < 0 || targetIndex >= openings.length) return openings

  const next = [...openings]
  const [moved] = next.splice(index, 1)
  next.splice(targetIndex, 0, moved)
  return next
}

/** Removes the opening with `id`. Immutable. Returns the original array reference when not found. */
export function removeOpening(openings: ConversationIntro[], id: string): ConversationIntro[] {
  if (!openings.some((opening) => opening.id === id)) return openings
  return openings.filter((opening) => opening.id !== id)
}

/**
 * Replaces the content of the opening with `id`. Immutable. Returns the
 * original array reference when not found.
 */
export function updateOpening(openings: ConversationIntro[], id: string, content: string): ConversationIntro[] {
  if (!openings.some((opening) => opening.id === id)) return openings
  return openings.map((opening) => (opening.id === id ? { ...opening, content } : opening))
}

/** Blank means content with no non-whitespace characters — the shared predicate. */
export function isBlankOpening(opening: ConversationIntro): boolean {
  return isBlankIntroContent(opening.content)
}

export type OpeningsValidationViolation =
  | { code: 'too_many'; count: number; max: number }
  | { code: 'multiple_blank'; blankIds: string[] }

export interface OpeningsValidationResult {
  valid: boolean
  violations: OpeningsValidationViolation[]
}

/**
 * Reports (does not repair) violations of the two published/finalized-state
 * invariants: at most ten openings, and at most one blank opening. A manually
 * edited draft may temporarily violate the blank rule; this helper only
 * reports it so the UI can surface a validation message and block publish —
 * it never deletes or rewrites an opening.
 */
export function validateOpenings(openings: ConversationIntro[]): OpeningsValidationResult {
  const violations: OpeningsValidationViolation[] = []

  if (openings.length > MAX_OPENINGS) {
    violations.push({ code: 'too_many', count: openings.length, max: MAX_OPENINGS })
  }

  const blankIds = openings.filter(isBlankOpening).map((opening) => opening.id)
  if (blankIds.length > 1) {
    violations.push({ code: 'multiple_blank', blankIds })
  }

  return { valid: violations.length === 0, violations }
}

/**
 * The legacy `initial_message` compatibility projection: the first nonblank
 * opening's content in creator-defined order, or `''` when none exists.
 */
export function deriveInitialMessageProjection(openings: ConversationIntro[]): string {
  return openings.find((opening) => !isBlankOpening(opening))?.content ?? ''
}
